import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Phone, CheckCircle, ChevronRight, Star, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { companyInfo, credentials, servicesList, serviceAreas } from '../data/mock';
import { serviceImages, serviceDescriptions, serviceFeatures, serviceBenefits, serviceFAQs, serviceProcess } from '../data/serviceData';
import { serviceSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';
import GuideDownloadCTA from '../components/GuideDownloadCTA';
import ServiceSchema from '../components/ServiceSchema';
import { CityLinksGrid, BlogLinkBanner } from '../components/InternalLinks';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesList.find(s => s.id === serviceId);
  
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const images = serviceImages[serviceId] || {};
  const description = serviceDescriptions[serviceId] || '';
  const features = serviceFeatures[serviceId] || [];
  const benefits = serviceBenefits[serviceId] || [];
  const faqs = serviceFAQs[serviceId] || [];
  const otherServices = servicesList.filter(s => s.id !== serviceId);
  const seo = serviceSEO[serviceId] || {};

  return (
    <div>
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={`/services/${serviceId}`}
      />
      
      {/* Comprehensive Schema Markup for LLMs, AI, GEO, AEO, Local SEO */}
      <ServiceSchema serviceId={serviceId} serviceName={service.title} />

      {/* Hero Section */}
      <section className="relative py-24 bg-neutral-900">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url('${images.heroImage}')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/90 to-neutral-900/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-8">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services" className="hover:text-amber-400">Services</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">{service.shortTitle}</span>
          </nav>
          
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{seo.h1 || `${service.title} in Dallas`}</h1>
            <p className="text-xl text-neutral-300 mb-8">{description} Trusted by Dallas homeowners for over 55 years.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-lg">
                  Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 font-semibold px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />{companyInfo.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Overview */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-neutral-900 mb-6">Professional {service.shortTitle} Services in Dallas-Fort Worth</h2>
                <p className="text-neutral-600 text-lg mb-8 leading-relaxed">
                  Gray Contracting delivers expert {service.shortTitle.toLowerCase()} services throughout the Dallas-Fort Worth metroplex. With over 55 years of experience, our licensed and insured team brings unmatched craftsmanship to every project. Whether you're in Dallas, Plano, Frisco, Richardson, or any surrounding community, we're committed to exceeding your expectations.
                </p>
                <div className="relative rounded-2xl overflow-hidden mb-8">
                  <img src={images.image} alt={`${service.title} in Dallas TX`} className="w-full h-80 object-cover" />
                </div>
              </div>

              {/* Features */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Our {service.shortTitle} Services Include</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 bg-neutral-50 p-4 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Benefits of Professional {service.shortTitle}</h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Star className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-neutral-700 mt-2">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Our {service.shortTitle} Process</h2>
                <div className="space-y-4">
                  {serviceProcess.map((step) => (
                    <div key={step.step} className="flex gap-4 bg-neutral-50 p-6 rounded-lg">
                      <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-neutral-900 mb-1">{step.title}</h3>
                        <p className="text-neutral-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQs */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-neutral-900 mb-6">Frequently Asked Questions About {service.shortTitle} in Dallas</h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`}>
                      <AccordionTrigger className="text-left font-semibold text-neutral-900 hover:text-amber-600">{faq.q}</AccordionTrigger>
                      <AccordionContent className="text-neutral-600">{faq.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Service Areas - linked to city pages */}
              <div className="mb-16">
                <CityLinksGrid serviceId={serviceId} shortTitle={service.shortTitle} />
              </div>

              {/* Blog Guide Link */}
              <BlogLinkBanner serviceId={serviceId} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-neutral-900 text-white rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-bold mb-4">Get a Free Estimate</h3>
                <p className="text-neutral-300 mb-6">Ready to start your {service.shortTitle.toLowerCase()} project? Contact us today!</p>
                <Link to="/contact">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold mb-4">
                    Request Quote <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone}`} className="block">
                  <Button variant="outline" className="w-full border-white text-white hover:bg-white hover:text-neutral-900">
                    <Phone className="mr-2 w-4 h-4" />{companyInfo.phone}
                  </Button>
                </a>
                
                <div className="border-t border-neutral-800 mt-6 pt-6">
                  <div className="text-sm text-neutral-400 mb-3">Why Choose Us:</div>
                  <div className="space-y-2">
                    {credentials.map((cred, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-amber-500" /><span>{cred}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-neutral-900 mb-4">Other Services</h3>
                <div className="space-y-3">
                  {otherServices.map((s) => (
                    <Link key={s.id} to={`/services/${s.id}`} className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-amber-50 transition-colors group">
                      <span className="font-medium text-neutral-700 group-hover:text-amber-600">{s.shortTitle}</span>
                      <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-amber-600" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Guide Download CTA */}
              <GuideDownloadCTA serviceId={serviceId} className="mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready for Professional {service.shortTitle} in Dallas?</h2>
          <p className="text-white/90 text-lg mb-8">Get a free estimate from Dallas's most trusted contractor. Over 55 years of excellence.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />{companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
